import { useContext, useEffect } from "react"
import { FastField, Field, useFormikContext } from "formik"
import { DateTime } from "luxon"
import _ from "lodash"
import { FormContext } from "../../context/formContext"
import { Calendar } from "primereact/calendar"

// Helpers
import fieldVisibility from "../../helpers/fieldVisibility"
import checkIfRequired from "./helpers/checkIfRequired"
import {
  DATE_SUBTYPES,
  resolveFieldSubtype,
  transformDate,
} from "../../helpers/dateNormalization"
import {
  helperStates,
  useServiceDateValidation,
} from "../../helpers/serviceDateValidationHelper"
import { UserContext } from "../../context/userContext"
import { LoaderSmall } from "../Loaders"

import styles from "../../styles/Form.module.css"

// Normalize and set a TZ-safe Date via transformDate
// PrimeReact Calendar returns a date with the time set to local midnight when showTime is false.
// We pass the value through transformDate for consistency: transformDate replaces that
// local-midnight time with local-noon, matching other parts of the app that treat
// date-only values as midday.
function transformAndSetDate(formik, fieldName, e, fieldDef) {
  // Get the effective subtype
  const subtype = resolveFieldSubtype(fieldDef)

  // For time-only and datetime fields, we can skip date transformation
  if (subtype === DATE_SUBTYPES.TIME || subtype === DATE_SUBTYPES.DATETIME) {
    return formik.setFieldValue(fieldName, e?.value || "")
  }

  // For date-only fields, we need to transform the value to ensure it's TZ-safe
  // This will convert the date to local noon, ensuring it's not accidentally shifted by timezone issues
  const raw = e?.value && e.value.toISOString ? e.value.toISOString() : e.value
  const transformed = transformDate(raw, "date", DATE_SUBTYPES.DATE)
  return formik.setFieldValue(fieldName, transformed)
}

function getYearRange(fieldName) {
  const currentYear = new Date().getFullYear()
  if (fieldName === "activity_date" || fieldName === "manage_services_date") {
    return `${currentYear - 1}:${currentYear}`
  }
  return `${currentYear - 101}:${currentYear + 1}`
}

function getCalendarProps(field) {
  const subtype = resolveFieldSubtype(field)
  const isTimeOnly = subtype === DATE_SUBTYPES.TIME
  const isDateTime = subtype === DATE_SUBTYPES.DATETIME

  return {
    showTime: isTimeOnly || isDateTime,
    timeOnly: isTimeOnly,
    hourFormat: isTimeOnly || isDateTime ? "12" : undefined,
    monthNavigator: !isTimeOnly,
    yearNavigator: !isTimeOnly,
    yearRange: !isTimeOnly ? getYearRange(field.field_name) : undefined,
  }
}

function getCalendarValue(value, field) {
  // Return empty string if no value or no field (to avoid passing undefined to Calendar component)
  if (!value || !field) return ""

  if (_.isDate(value)) {
    return value
  } else {
    // If not already transformed, transform the value for display in the calendar
    const subtype = resolveFieldSubtype(field)
    return transformDate(value, "date", subtype)
  }
}

const useActivityDateEffects = (formik, field) => {
  // Sync activity_end_date when activity_date changes
  useEffect(() => {
    if (formik.values.activity_date && formik.values.activity_end_date) {
      const startDate = DateTime.fromJSDate(formik.values.activity_date)
      const endDate = DateTime.fromJSDate(formik.values.activity_end_date)
      if (startDate.startOf("day") > endDate.startOf("day")) {
        formik.setFieldValue("activity_end_date", formik.values.activity_date)
      }
    }
    if (
      formik.values.activity_date &&
      formik.values.activity_multiday === "No"
    ) {
      formik.setFieldValue("activity_end_date", formik.values.activity_date)
    }
    if (formik.values.activity_multiday === "") {
      formik.setFieldValue("activity_end_date", formik.values.activity_date)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.activity_date])

  // Sync activity_end_date when activity_multiday changes
  useEffect(() => {
    if (formik.values.activity_multiday === "No") {
      formik.setFieldValue("activity_end_date", formik.values.activity_date)
    }
    if (
      formik.values.activity_multiday === "Yes" &&
      formik?.values?.activity_end_date === ""
    ) {
      formik.setFieldValue("activity_end_date", formik.values.activity_date)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.activity_multiday])

  // Initialize activity_date and activity_end_date on mount
  useEffect(() => {
    if (
      field.field_name === "activity_date" ||
      field.field_name === "activity_end_date"
    ) {
      const fieldSessionDate = formik?.getFieldProps(field.field_name)
      if (!_.isDate(fieldSessionDate.value)) {
        const initialValue = DateTime.now()
        const now = initialValue.toUTC().toJSDate()
        const end = initialValue.plus({ minutes: 15 }).toUTC().toJSDate()
        formik.setFieldValue(field.field_name, now)
        formik.setFieldValue("activity_date", now)
        formik.setFieldValue("activity_end_date", end)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

const getMinDate = (fieldName, formikValues) => {
  const now = DateTime.now()
  switch (fieldName) {
    case "account_birthdate":
    case "demo_birthdate":
      const minAge = now.minus({ years: 101 })
      return minAge.toJSDate()
    case "activity_end_date":
      return formikValues.activity_date
    default:
      return null
  }
}

const getMaxDate = (fieldName) => {
  const now = DateTime.now()
  switch (fieldName) {
    case "account_birthdate":
    case "demo_birthdate":
      const maxAge = now.minus({ years: 12 })
      return maxAge.toJSDate()
    default:
      return null
  }
}

function RegularFieldOutput(props) {
  const { field, block, index } = props
  const form = useContext(FormContext)
  const formik = useFormikContext()
  const userCtx = useContext(UserContext)
  const calendarProps = getCalendarProps(field)

  useActivityDateEffects(formik, field)
  const {
    status: serviceDateValidationStatus,
    initError: serviceDateValidationError,
    initializeServiceDateValidation,
    checkServiceDate,
  } = useServiceDateValidation()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  useEffect(() => {
    // Determine if field is required
    const isRequired = checkIfRequired(props.field.field_validation.required[0])

    // Consolidated birthdate sync for account_birthdate and demo_birthdate
    const syncBirthdate = () => {
      const isAccount = field.field_name === "account_birthdate"
      const isDemo = field.field_name === "demo_birthdate"
      if (!isAccount && !isDemo) return

      const sourceVal = isAccount
        ? form?.globalValues?.participant__demographics?.demo_birthdate
        : form?.globalValues?.participant?.account_birthdate

      const targetVal = isAccount
        ? form?.globalValues?.participant?.account_birthdate
        : form?.globalValues?.participant__demographics?.demo_birthdate

      if (!sourceVal) return

      if (sourceVal !== targetVal) {
        const isoBirthdate = new Date(sourceVal)
        const targetField = isAccount ? "account_birthdate" : "demo_birthdate"
        const targetValDate = new Date(formik.values[targetField])

        // Only update if values differ
        if (targetValDate.getTime() !== isoBirthdate.getTime()) {
          formik.setFieldValue(targetField, isoBirthdate).then(() => {
            if (isRequired) {
              formik.setFieldTouched(targetField, true)
              formik.validateField(targetField)
            }
          })
        }
      }
    }

    syncBirthdate()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const init = async () => {
      // Register field as loading in FormContext
      form && form.registerLoadingField(props.field)

      // Initialize service date validation
      await initializeServiceDateValidation({
        participant_membership_id: userCtx?.participant_membership_id,
        idTemplate: form?.idTemplate || null,
        field_name: field.field_name,
        isEdit: form?.editMode || false,
      })

      // Notify FormContext that field is loaded
      form && form.completeLoadingField(props.field)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isRequired = checkIfRequired(props.field.field_validation.required[0])

  if (
    serviceDateValidationStatus !== helperStates.READY &&
    !serviceDateValidationError
  ) {
    return <LoaderSmall />
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field
          name={field.field_name}
          validate={(value) => {
            return checkServiceDate({
              checkDate: value,
            })
          }}
        >
          {({ field: formikField, meta }) => {
            const jsDate = getCalendarValue(formikField.value, field)

            return (
              <>
                <Calendar
                  name={formikField.name}
                  value={jsDate}
                  onChange={(e) => {
                    // Sync globalValues when birthdate fields change
                    if (
                      field.field_name === "account_birthdate" &&
                      form?.globalValues?.participant__demographics
                        ?.demo_birthdate
                    ) {
                      form.globalValues.participant__demographics.demo_birthdate =
                        e.value
                    } else if (
                      field.field_name === "demo_birthdate" &&
                      form?.globalValues?.participant?.account_birthdate
                    ) {
                      form.globalValues.participant.account_birthdate = e.value
                    }

                    transformAndSetDate(formik, formikField.name, e, field)
                  }}
                  disabled={isDisabled}
                  onBlur={formikField.onBlur}
                  maxDate={getMaxDate(field.field_name)}
                  minDate={getMinDate(field.field_name, formik.values)}
                  {...calendarProps}
                />
                {meta.touched && meta.error && !formik.isValidating && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </Field>
      </div>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, block, index } = props
  const form = useContext(FormContext)
  const formik = useFormikContext()
  const calendarProps = getCalendarProps(field)
  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  useActivityDateEffects(formik, field)

  const isRequired = checkIfRequired(props.field.field_validation.required[0])

  const { mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            const jsDate = getCalendarValue(fastField.value, field)

            return (
              <>
                <Calendar
                  name={fastField.name}
                  value={jsDate}
                  onChange={(e) => {
                    transformAndSetDate(formik, fastField.name, e, field)
                  }}
                  disabled={isDisabled}
                  onBlur={fastField.onBlur}
                  maxDate={getMaxDate(field.field_name)}
                  minDate={getMinDate(field.field_name, formik.values)}
                  {...calendarProps}
                />
                {meta.touched && meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

function NestedMultiFieldOutput(props) {
  const {
    field,
    isRequired,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const formik = useFormikContext()
  const calendarProps = getCalendarProps(field)
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, meta }) => {
            const jsDate = getCalendarValue(fastField.value, field)

            return (
              <>
                <Calendar
                  name={fastField.name}
                  value={jsDate}
                  onChange={(e) => {
                    transformAndSetDate(formik, fastField.name, e, field)
                  }}
                  onBlur={fastField.onBlur}
                  {...calendarProps}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

export default function TypeDate(props) {
  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    case "nested-multi-field":
      const nestedMultiFieldProps = {
        field: props.field,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />
    default:
      return null
  }
}
